import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../lib/firebase';
import type { MeResponse } from '../lib/api';
import { hasPerm } from '../lib/api';
import { Icon, type IconName } from './Icons';
import { PageSkeleton } from './Skeleton';

type NavItem = { to: string; label: string; short: string; perm: string; icon: IconName };

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', short: 'Início', perm: 'menu.dashboard', icon: 'dashboard' },
  { to: '/analytics', label: 'Analytics', short: 'Métricas', perm: 'menu.analytics', icon: 'analytics' },
  { to: '/users', label: 'Usuários', short: 'Usuários', perm: 'menu.users', icon: 'users' },
  { to: '/payments', label: 'Pagamentos', short: 'Pagtos', perm: 'menu.payments', icon: 'payments' },
  { to: '/subscriptions', label: 'Assinaturas', short: 'Assinat.', perm: 'menu.payments', icon: 'payments' },
  { to: '/support', label: 'Suporte', short: 'Suporte', perm: 'menu.support', icon: 'support' },
  { to: '/licenses', label: 'Licenças', short: 'Licenças', perm: 'menu.licenses', icon: 'licenses' },
  { to: '/coupons', label: 'Cupons', short: 'Cupons', perm: 'menu.coupons', icon: 'coupons' },
  { to: '/marketing', label: 'Marketing', short: 'Mkt', perm: 'menu.marketing', icon: 'marketing' },
  { to: '/cart-recovery', label: 'Carrinho abandonado', short: 'Carrinho', perm: 'menu.marketing', icon: 'marketing' },
  { to: '/affiliates', label: 'Afiliados', short: 'Afiliados', perm: 'menu.affiliates', icon: 'affiliates' },
  { to: '/linktree', label: 'Linktree', short: 'Links', perm: 'menu.linktree', icon: 'linktree' },
  { to: '/ai', label: 'Assistente IA', short: 'IA', perm: 'menu.ai', icon: 'ai' },
  { to: '/support-ai', label: 'IA Suporte', short: 'IA Sup.', perm: 'menu.support_ai', icon: 'supportAi' },
  { to: '/access', label: 'Acesso', short: 'Acesso', perm: 'menu.access', icon: 'access' },
  { to: '/releases', label: 'Releases & CDN', short: 'Releases', perm: 'menu.releases', icon: 'releases' },
];

/** Sub-rotas que não aparecem no menu mas herdam a permissão do módulo. */
const NESTED_ROUTE_PERMS: Array<[RegExp, string]> = [
  [/^\/users\/[^/]+$/, 'menu.users'],
];

/**
 * Permissão exigida por um caminho. `undefined` significa rota desconhecida,
 * que é tratada como bloqueada — mesma postura do fail-closed da API.
 */
function permForPath(pathname: string): string | undefined {
  const exact = NAV.find((n) => n.to === pathname);
  if (exact) return exact.perm;
  for (const [pattern, perm] of NESTED_ROUTE_PERMS) {
    if (pattern.test(pathname)) return perm;
  }
  return undefined;
}

function AccessDenied({ known }: { known: boolean }) {
  return (
    <div className="ops-page">
      <div className="card denied-card">
        <div className="denied-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <rect x="4.5" y="10.2" width="15" height="10.3" rx="2.5" />
            <path d="M8 10.2V7.6a4 4 0 0 1 8 0v2.6" />
          </svg>
        </div>
        <h2>{known ? 'Sem permissão' : 'Página não encontrada'}</h2>
        <p className="muted">
          {known
            ? 'Seu perfil não tem acesso a este módulo. Fale com um administrador se precisar dele.'
            : 'Este endereço não corresponde a nenhum módulo do console.'}
        </p>
      </div>
    </div>
  );
}

export function Layout({ me }: { me: MeResponse }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('menu-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('menu-open');
    };
  }, [menuOpen]);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const navItems = NAV.filter((n) => hasPerm(me.permissions, n.perm));
  // A tab bar segue a convenção iOS: poucos destinos diretos e um "Mais".
  const tabItems = navItems.slice(0, 4);

  const requiredPerm = permForPath(location.pathname);
  const allowed = requiredPerm !== undefined && hasPerm(me.permissions, requiredPerm);

  return (
    <div className={`layout ${menuOpen ? 'layout--menu-open' : ''}`}>
      <header className="mobile-topbar">
        <button
          type="button"
          className="menu-toggle"
          aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className="mobile-brand">
          <img src="/icons/icon-192.png" alt="" width={26} height={26} className="brand-mark" />
          <strong>NEXT<span>BOOST</span></strong>
        </div>
        <span className="mobile-role muted">{me.role_label}</span>
      </header>

      <div
        className="sidebar-backdrop"
        role="presentation"
        onClick={() => setMenuOpen(false)}
        hidden={!menuOpen}
      />

      <aside className="sidebar" aria-label="Navegação principal">
        <div className="sidebar-brand">
          <img src="/icons/icon-192.png" alt="" width={34} height={34} className="brand-mark" />
          <h1>NEXT<span>BOOST</span></h1>
        </div>
        <p className="sidebar-user">
          {me.display_name || me.email}
          <br />
          <small>{me.role_label}</small>
        </p>
        <nav>
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'}>
              <Icon name={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <button type="button" className="secondary logout-btn" onClick={handleLogout}>
          Sair
        </button>
      </aside>

      <main className="main">
        {/* A key força o replay da animação a cada rota, dando continuidade ao gesto */}
        <div className="page-fade" key={location.pathname}>
          {/* O bloqueio acontece antes do Suspense: a página nem é montada,
              então nenhuma requisição sai para um módulo sem permissão. */}
          {allowed ? (
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          ) : (
            <AccessDenied known={requiredPerm !== undefined} />
          )}
        </div>
      </main>

      <nav className="tabbar" aria-label="Navegação rápida">
        {tabItems.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === '/'}>
            <Icon name={item.icon} />
            {item.short}
          </NavLink>
        ))}
        <a
          href="#menu"
          className={menuOpen ? 'active' : ''}
          onClick={(e) => { e.preventDefault(); setMenuOpen(true); }}
          aria-label="Mais opções"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="nav-icon" aria-hidden="true">
            <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
            <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
            <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
          </svg>
          Mais
        </a>
      </nav>
    </div>
  );
}
